import * as React from "react";
import { PATIENT_OVERVIEW_EDIT_WINDOW } from "../../../store/PatientInformationForm/type";
import * as moment from "moment";
import {
    AddressCheckin,
    PhoneCheckin,
    EmailCheckin
} from "../../../dtos/KioskDtos.dtos";
import { IPatientOverviewProps, IOrientationProps } from "../Types";
import { DataTableRender } from "./DataTableRender";
import MaskedFormat from "../../../services/MaskedFormat";
import { IPatientOverviewLang } from "../../../services/Language/Types";
import { ValidationBlock } from "../../FormControls/ValidationBlock";
import { MediaOrientation } from "../../../hooks/useMediaQuery.hook";
import { DefaultCheckbox } from "../../customFormComponents/CheckBoxGroup/Checkbox";
import {ic_error} from 'react-icons-kit/md/ic_error'
import {ic_check_circle} from 'react-icons-kit/md/ic_check_circle'
import { CustomAlert } from "../../genericComponents/CustomAlert";

export const DemographicsTable = (props: IPatientOverviewProps): JSX.Element => {
    const textString = props.text;
    const isHealthCardExpired = props.patientInfo.healthCard.expiryDate
        && moment(props.patientInfo.healthCard.expiryDate).utc()
        .isBefore(moment().utc(), "month")

    const renderElement = (title: string, element: string, applyValidationStyle: boolean = false) => {
        return (
            <ul style={{ padding: "0", listStyleType: "none" }}>
                <li>
                    <span className="attributeLabel">{title + " "}</span>
                </li>
                <li style={{ color: applyValidationStyle ? "red" : null, fontSize: "25px" }}>
                    <span>{element}</span>
                </li>
            </ul>
        )
    }
    return (
        <div>
            <table className="table">
                <thead>
                    <tr>
                        <th colSpan={4}>
                            <span className="category-title">{textString.titles.DemographicsTitle}</span>
                            {
                                props.isPersonalModificationOptionActive &&
                                <button
                                    style={{ float: "right" }}
                                    className="buttons-white-smaller"
                                    onClick={() => props.showEditModal(true, PATIENT_OVERVIEW_EDIT_WINDOW.DEMOGRAPHICS)}
                                >
                                    {
                                        props.text.labels.EditLabel
                                    }
                                </button>
                            }
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <div className="flex-wrap">
                                <div className="flex-grow-1">
                                    {renderElement(
                                        props.countryMappedStrings.healthCardIdLabel.CA,
                                        props.patientInfo.healthCard.number)
                                    }
                                </div>
                                <div className="flex-grow-1">
                                    {
                                        renderElement(
                                            textString.labels.ExpDateLabel,
                                            !!props.patientInfo.healthCard.expiryDate
                                                ? moment(props.patientInfo.healthCard.expiryDate).utc().locale(props.lang).format("YYYY-MM")
                                                : "",
                                                isHealthCardExpired
                                            )
                                    }
                                </div>
                                <div className="flex-grow-1">
                                    {
                                        renderElement(
                                            textString.labels.DateOfBirthLabel,
                                            moment(props.patientInfo.demographics.dateOfBirth).utc().format("YYYY-MM-DD")
                                        )
                                    }
                                </div>
                                <div className="flex-grow-1">
                                    {renderElement(
                                        textString.labels.PreferredNameLabel,
                                        props.patientInfo.demographics.preferredName
                                    )}
                                </div>
                                <div className="flex-grow-1">
                                    {renderElement(
                                        textString.labels.PreferredPronounsLabel,
                                        props.patientInfo.demographics.specifiedPreferredPronouns.length != 0 ?
                                            props.patientInfo.demographics.specifiedPreferredPronouns :
                                            props.patientOverviewStrings.preferredPronouns[props.patientInfo.demographics.preferredPronouns]
                                    )}
                                </div>
                                <div className="flex-grow-1">
                                    {renderElement(
                                        textString.labels.SexAtBirthLabel,
                                        props.patientOverviewStrings.sexAtBirth[props.patientInfo.demographics.sexAtBirth]
                                    )}
                                </div>
                                <div className="flex-grow-1">
                                    {renderElement(
                                        textString.labels.GenderIdentityLabel,
                                        props.patientOverviewStrings.genderIdentity[props.patientInfo.demographics.genderIdentity]
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="col-sm-12 text-center">
                                    <span style={{ width: "70%" }}>
                                        <ValidationBlock
                                            isValid={!isHealthCardExpired}
                                            customClass={"validation-block"}
                                            validationMessage={textString.validations.ExpiredDateValidation}
                                        />
                                    </span>
                                </div>
                            </div >
                        </td>
                    </tr>
                </tbody>
            </table>
        </div >
    );
}

// tslint:disable-next-line: no-shadowed-variable
export const AddressData = (addr: AddressCheckin, lang: IPatientOverviewLang, orientation: MediaOrientation): JSX.Element => {
    switch (orientation) {
        case MediaOrientation.Portrait:
            return (
                <ul>
                    <span className="attributeLabel">{lang.contactPoint[addr.contactPointType]}{addr.isPreferred && "*"}</span>
                    <li>{addr.streetAddress}</li>
                    <li>{addr.city + (addr.country && addr.state ? ", " + lang.regions[addr.country][addr.state] : "")}</li>
                    <li>{addr.zipCode}</li>
                    <li>{addr.country && lang.country[addr.country]}</li>
                </ul>
            );
        case MediaOrientation.Landscape:
            const address: string = [
                addr.streetAddress,
                addr.city,
                (addr.country && addr.state ? lang.regions[addr.country][addr.state] : ""),
                addr.zipCode,
                (addr.country ? (lang.country[addr.country]) : "")
            ]
                .filter(p => p)
                .join(", ");

            return (
                <ul>
                    <span className="attributeLabel">{lang.contactPoint[addr.contactPointType]}{addr.isPreferred && "*"}</span>
                    <li>{address}</li>
                </ul>
            );
        default:
            return null;
    }
};

export const AddressesTable = (props: IPatientOverviewProps & IOrientationProps): JSX.Element => (
    <DataTableRender
        orientation={props.orientation}
        canModify={props.isPersonalModificationOptionActive}
        textTitle={props.text.titles.AddressTitle}
        editText={props.text.labels.EditLabel}
        addText={props.text.labels.AddLabel}
        deleteText={props.text.labels.DeleteButton}
        dataToRender={(data: any) => AddressData(data, props.patientOverviewStrings, props.orientation)}
        dataArray={props.patientInfo.addresses}
        onEditItem={() => { props.showEditModal(true, PATIENT_OVERVIEW_EDIT_WINDOW.ADDRESS) }}
    />
);

export const PhoneNumberData = (phone: PhoneCheckin, lang: IPatientOverviewLang, renderNotifications: boolean): JSX.Element => (
    <ul>
        <li>
            <span className="attributeLabel">{lang.contactPoint[phone.contactPointType] + "-" + lang.phoneType[phone.phoneType]}{phone.isPreferred && "*"}</span>
        </li>
        <li>
            <span>{MaskedFormat.convertRawNumberToCountryPhoneNumberView(phone.number, phone.countryLookupId)}</span>
        </li>
        {
            phone.extension &&
            <li>
                <span>{" Ext. " + phone.extension}</span>
            </li>
        }
        {renderNotifications && phone.phoneType === "Mobile" &&
            <li>
                <DefaultCheckbox checked={phone.notificationsEnabled} disabled label={lang.notifications} />
            </li>
        }
    </ul>
);

export const PhoneNumbersTable = (props: IPatientOverviewProps & IOrientationProps): JSX.Element => (
    <DataTableRender
        orientation={props.orientation}
        removeLeftPadding={true}
        canModify={props.isPersonalModificationOptionActive}
        textTitle={props.text.titles.PhoneTitle}
        editText={props.text.labels.EditLabel}
        addText={props.text.labels.AddLabel}
        deleteText={props.text.labels.DeleteButton}
        dataToRender={(data: any) => PhoneNumberData(data, props.patientOverviewStrings, props.myleClinicSettings.enablePatientNotifiableContactPoints)}
        dataArray={props.patientInfo.phones}
        onEditItem={() => props.showEditModal(true, PATIENT_OVERVIEW_EDIT_WINDOW.PHONE)}
    />
);


export const EmailData = (email: EmailCheckin, lang: IPatientOverviewLang, renderNotifications: boolean): JSX.Element => (
    <ul>
        <li>
            <span className="attributeLabel">{lang.contactPoint[email.contactPointType]}</span>
        </li>
        <li>
            <span style={{ overflowWrap: "break-word" }}>{email.address}</span>
        </li>
        {renderNotifications &&
            <li>
                <DefaultCheckbox checked={email.notificationsEnabled} disabled label={lang.notifications} />
            </li>
        }
    </ul>
);

export const EmailsTable = (props: IPatientOverviewProps & IOrientationProps): JSX.Element => (
    <DataTableRender
        orientation={props.orientation}
        removeLeftPadding={true}
        canModify={props.isPersonalModificationOptionActive}
        addText={props.text.labels.AddLabel}
        editText={props.text.labels.EditLabel}
        textTitle={props.text.titles.EmailTitle}
        deleteText={props.text.labels.DeleteButton}
        dataToRender={(data: any) => EmailData(data, props.patientOverviewStrings, props.myleClinicSettings.enablePatientNotifiableContactPoints)}
        dataArray={props.patientInfo.emails}
        onEditItem={() => props.showEditModal(true, PATIENT_OVERVIEW_EDIT_WINDOW.EMAIL)}
    />
);

export const PreferredPharmacyTable = (props: IPatientOverviewProps): JSX.Element => {
    const textString = props.text;
    const preferredPharmacy = props.patientInfo.preferredPharmacy;

    return (
        <div>
            <table className="table">
                <thead>
                    <tr>
                        <th colSpan={4}>
                            <span className="category-title">{textString.titles.PreferredPharmacyTitle}</span>
                            {props.isPreferredPharmacyOptionActive && (
                                <button
                                    style={{ float: "right" }}
                                    className="buttons-white-smaller"
                                    onClick={() => props.showEditModal(true, PATIENT_OVERVIEW_EDIT_WINDOW.PREFERRED_PHARMACY)}
                                >
                                    { props.text.labels.EditLabel }
                                </button>
                            )}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ paddingTop: "10px" }}>
                            {preferredPharmacy && (
                                <div style={{ padding: "10px 0" }}>
                                    <ul style={{ padding: 0, listStyleType: "none" }}>
                                        <strong style={{ color: "#084a8c", fontSize: "25px" }}>{preferredPharmacy.banner}</strong>
                                        <li>{preferredPharmacy.address}</li>
                                        <li>{`T: ${preferredPharmacy.phone} | F: ${preferredPharmacy.fax}`}</li>
                                    </ul>
                                </div>
                            )}
                            {!preferredPharmacy && (
                                <CustomAlert
                                    bsStyle="warning"
                                    icon={ic_error}
                                    message={props.text.messagesInstructions.NoPharmacyAssociatedToFileMessage}
                                />
                            )}
                            {preferredPharmacy && preferredPharmacy.isMylePharmacy && (
                                <CustomAlert
                                    bsStyle="success"
                                    icon={ic_check_circle}
                                    message={props.text.messagesInstructions.GreatNewsMessage}
                                    additionalContent={(
                                        <>
                                            <p>{props.text.messagesInstructions.PharmacyOfferOnlinePrescriptionPlanning}</p>
                                            <br/>
                                            <strong>{props.text.messagesInstructions.NotificationToReviewYourPrescriptionWillBeSent}</strong>
                                        </>
                                    )}
                                />
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div >
    );
}



// WEBPACK FOOTER //
// ./src/components/PatientOverview/SubComponents/PatientOverviewInformationTable.tsx